/**
 * Shared heterojob mapping for cost-per-part charts.
 * Same shape everywhere: { capex, recurring, material, consumables, total }.
 */

/** Map pre-calculated cppData row (from SecondReport) to per-job chart shape */
export function mapCppRowToPerJob(row) {
  if (!row) return null;
  return {
    capex: parseFloat(row.system_cost_per_cm3) || 0,
    recurring: parseFloat(row.recurring_cost_per_cm3) || 0,
    material: parseFloat(row.material_cost_per_cm3) || 0,
    consumables: parseFloat(row.consumable_cost_per_cm3) || 0,
    total: parseFloat(row.total_am_cost_cm3) || 0,
  };
}

/** Volume for one job: Metal = part_volume + part_support_volume, Polymer = part_volume */
export function getVolumeForJob(job) {
  const volSum =
    parseFloat(job?.part_volume || 0) + parseFloat(job?.part_support_volume || 0);
  return volSum > 0 ? volSum : parseFloat(job?.part_volume || 0);
}

/** Check if cppData is valid for chart (same length as visible hetrojobs, rows have total) */
export function isValidCppData(cppData, expectedLength) {
  return (
    Array.isArray(cppData) &&
    cppData.length === expectedLength &&
    cppData.every((row) => row && row.total_am_cost_cm3 != null)
  );
}

/** Build per-job values from scenario data (cost per cm³ × volume per job) */
export function buildPerJobFromScenarioData(data, visibleHetrojobs) {
  if (data?.total_am_cost_cm3?.value == null) return null;
  const sys = parseFloat(data?.system_cost_per_cm3?.value || 0);
  const rec = parseFloat(data?.recurring_cost_per_cm3?.value || 0);
  const mat = parseFloat(data?.material_cost_per_cm3?.value || 0);
  const cons = parseFloat(data?.consumable_cost_per_cm3?.value || 0);
  const totalPerCm3 = parseFloat(data?.total_am_cost_cm3?.value || 0);
  return visibleHetrojobs.map((job) => {
    const volume = getVolumeForJob(job);
    return {
      capex: sys * volume,
      recurring: rec * volume,
      material: mat * volume,
      consumables: cons * volume,
      total: totalPerCm3 * volume,
    };
  });
}

/** Filter hetrojobs to visible rows (has volume or no_of_parts) */
export function getVisibleHetrojobs(hetrojobs) {
  if (!Array.isArray(hetrojobs)) return [];
  // Backend sometimes includes a single hetrojob row for the base part.
  // Only treat as "multi-part" when there are 2+ parts.
  if (hetrojobs.length <= 1) return [];
  return hetrojobs.filter((job) => {
    if (!job) return false;
    const volume = parseFloat(job.part_volume ?? job.part_support_volume ?? 0);
    const parts = parseFloat(job.no_of_parts ?? 0);
    return (Number.isFinite(volume) && volume > 0) || (Number.isFinite(parts) && parts > 0);
  });
}
